export const ORTHO_WIRE_CUSTOM_VALUE = "__custom";
export const ORTHO_WIRE_NOT_RECORDED_VALUE = "__not_recorded";
export const ORTHO_WIRE_NO_CHANGE_VALUE = "__no_change";

export const ORTHO_WIRE_MATERIAL_OPTIONS = [
  "NiTi",
  "CuNiTi",
  "Stainless Steel",
  "TMA",
  "Beta Titanium",
  "Other",
];

export const ORTHO_WIRE_SIZE_OPTIONS = [
  "0.010",
  "0.012",
  "0.014",
  "0.016",
  "0.018",
  "0.020",
  "0.014 × 0.025",
  "0.016 × 0.016",
  "0.016 × 0.022",
  "0.017 × 0.025",
  "0.018 × 0.022",
  "0.018 × 0.025",
  "0.019 × 0.025",
  "0.021 × 0.025",
];

export function canonicalWireMaterial(value) {
  const material = String(value || "").trim();
  if (material === "SS") return "Stainless Steel";
  return material;
}

function normalizeNumericPart(value) {
  const clean = String(value || "").trim();
  return clean.startsWith(".") ? `0${clean}` : clean;
}

// Normalize only an unambiguous numeric gauge. Descriptive doctor-entered
// values stay exactly as entered (apart from outside whitespace).
export function normalizeWireSize(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const rectangular = raw.match(/^(\.?\d+(?:\.\d+)?)\s*[xX×]\s*(\.?\d+(?:\.\d+)?)$/);
  if (rectangular) {
    return `${normalizeNumericPart(rectangular[1])} × ${normalizeNumericPart(rectangular[2])}`;
  }
  if (/^\.?\d+(?:\.\d+)?$/.test(raw)) return normalizeNumericPart(raw);
  return raw;
}

export function formatWireSelection(selection) {
  const material = canonicalWireMaterial(selection?.material);
  const size = normalizeWireSize([
    ORTHO_WIRE_CUSTOM_VALUE,
    ORTHO_WIRE_NOT_RECORDED_VALUE,
    ORTHO_WIRE_NO_CHANGE_VALUE,
  ].includes(selection?.size) ? "" : selection?.size);
  const custom = normalizeWireSize(selection?.custom);
  if (custom) return material && material !== "Other" ? `${material} ${custom}` : custom;
  if (size) return material && material !== "Other" ? `${material} ${size}` : size;
  return material && material !== "Other" ? material : "";
}

export function wireSelectionsEqual(left, right) {
  return canonicalWireMaterial(left?.material) === canonicalWireMaterial(right?.material)
    && normalizeWireSize(left?.size) === normalizeWireSize(right?.size)
    && String(left?.custom || "").trim() === String(right?.custom || "").trim();
}

export function wireSelectionFromOption(option) {
  if (!option) return { material: "", size: ORTHO_WIRE_NOT_RECORDED_VALUE, custom: "", optionId: "" };
  return {
    material: canonicalWireMaterial(option.material),
    size: normalizeWireSize(option.wire_size),
    custom: "",
    optionId: option.id || "",
  };
}

export function findWireOptionId(options, selection) {
  const material = canonicalWireMaterial(selection?.material);
  const size = normalizeWireSize(selection?.size);
  if (!material || !size || size === ORTHO_WIRE_CUSTOM_VALUE || selection?.custom) return "";
  return (Array.isArray(options) ? options : []).find((option) => (
    canonicalWireMaterial(option.material) === material
      && normalizeWireSize(option.wire_size) === size
  ))?.id || "";
}

function legacySelectionFromLabel(label, options) {
  const clean = String(label || "").replace(/^Other:\s*/i, "").trim();
  if (!clean) return null;
  const option = (Array.isArray(options) ? options : []).find((candidate) => (
    candidate.display_name === clean
      || `${candidate.wire_size || ""} ${candidate.material || ""}`.trim() === clean
  ));
  if (option) return wireSelectionFromOption(option);

  const materials = [...ORTHO_WIRE_MATERIAL_OPTIONS, "SS"]
    .filter((value) => value !== "Other")
    .sort((a, b) => b.length - a.length);
  const material = materials.find((candidate) => (
    clean.toLowerCase().startsWith(`${candidate.toLowerCase()} `)
      || clean.toLowerCase().endsWith(` ${candidate.toLowerCase()}`)
  ));
  if (material) {
    const size = clean
      .replace(new RegExp(`^${material}\\s+`, "i"), "")
      .replace(new RegExp(`\\s+${material}$`, "i"), "")
      .trim();
    return { material: canonicalWireMaterial(material), size: normalizeWireSize(size), custom: "", optionId: "" };
  }
  return { material: "Other", size: ORTHO_WIRE_CUSTOM_VALUE, custom: clean, optionId: "" };
}

export function wireSelectionFromVisit(visit, side, options = []) {
  if (!visit) return { material: "", size: ORTHO_WIRE_NOT_RECORDED_VALUE, custom: "", optionId: "" };
  const material = canonicalWireMaterial(visit[`${side}_wire_material`]);
  const size = normalizeWireSize(visit[`${side}_wire_size`]);
  const custom = String(visit[`${side}_wire_custom`] || "").trim();
  const optionId = visit[`${side}_wire_option_id`] || "";
  if (material || size || custom) {
    return {
      material: material || (custom ? "Other" : ""),
      size: custom ? ORTHO_WIRE_CUSTOM_VALUE : (size || ORTHO_WIRE_NOT_RECORDED_VALUE),
      custom,
      optionId,
    };
  }

  const option = optionId ? (Array.isArray(options) ? options : []).find((candidate) => candidate.id === optionId) : null;
  if (option) return wireSelectionFromOption(option);

  const legacyCustom = String(visit[`${side}_wire_other_text`] || "").trim();
  if (legacyCustom) {
    return { material: "Other", size: ORTHO_WIRE_CUSTOM_VALUE, custom: legacyCustom, optionId: "" };
  }

  return legacySelectionFromLabel(visit[`${side}_wire_label_snapshot`], options)
    || { material: "", size: ORTHO_WIRE_NOT_RECORDED_VALUE, custom: "", optionId: "" };
}

export function wireSelectionPayload(selection, options = []) {
  const custom = String(selection?.custom || "").trim();
  const isNotRecorded = selection?.size === ORTHO_WIRE_NOT_RECORDED_VALUE
    || (!selection?.material && !selection?.size && !custom);
  if (isNotRecorded) {
    return { material: null, size: null, custom: null, optionId: null };
  }
  const isCustom = selection?.size === ORTHO_WIRE_CUSTOM_VALUE || !!custom;
  const normalizedSize = isCustom ? null : (normalizeWireSize(selection?.size) || null);
  return {
    material: canonicalWireMaterial(selection?.material) || (isCustom ? "Other" : null),
    size: normalizedSize,
    custom: isCustom ? (custom || null) : null,
    optionId: isCustom ? null : (findWireOptionId(options, selection) || null),
  };
}
